// ═══════════════════════════════════════════════════════════════
// PROVISIONES — Tab Por Empleado
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import api from '../../../api/config';
import { useEmployeeProvisions } from '../../../hooks/useProvisions';

const fmt = (n) =>
  new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const CODIGO_COLORS = {
  REGAL: { bg: 'bg-amber-50',  border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700'  },
  VAC:   { bg: 'bg-blue-50',   border: 'border-blue-200',  badge: 'bg-blue-100 text-blue-700'    },
  CES:   { bg: 'bg-red-50',    border: 'border-red-200',   badge: 'bg-red-100 text-red-700'      },
  PREV:  { bg: 'bg-purple-50', border: 'border-purple-200',badge: 'bg-purple-100 text-purple-700'},
};

export default function ProvisionsEmployeeView({ companyId }) {
  const [employees, setEmployees]       = useState([]);
  const [searchTerm, setSearchTerm]     = useState('');
  const [selectedEmpId, setSelectedEmpId] = useState(null);

  // Cargar lista de empleados
  useEffect(() => {
    if (!companyId) return;
    api.get(`/employees?companyId=${companyId}&estado=activo`)
      .then(r => setEmployees(r.data?.employees || r.data || []))
      .catch(() => {});
  }, [companyId]);

  const filtered = employees.filter(e =>
    `${e.nombre} ${e.apellido}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (e.employee_id || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Panel izquierdo: buscador de empleados */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Seleccionar Empleado</h2>
        <input
          type="text"
          placeholder="Buscar por nombre o código..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 mb-3"
        />
        <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
          {filtered.map(emp => (
            <button
              key={emp.id}
              onClick={() => setSelectedEmpId(emp.id)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                selectedEmpId === emp.id
                  ? 'bg-amber-50 border border-amber-200 text-amber-800 font-medium'
                  : 'hover:bg-gray-50 text-gray-700 border border-transparent'
              }`}
            >
              <span className="block font-medium truncate">
                {emp.nombre} {emp.apellido}
              </span>
              <span className="text-xs text-gray-400">{emp.employee_id}</span>
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">Sin resultados</p>
          )}
        </div>
      </div>

      {/* Panel derecho: detalle del empleado */}
      <div className="lg:col-span-2">
        {selectedEmpId ? (
          <EmployeeProvisionDetail employeeId={selectedEmpId} companyId={companyId} />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-400">
            <p className="text-4xl mb-3">📦</p>
            <p className="font-medium">Selecciona un empleado para ver sus provisiones</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Detalle de un empleado ──────────────────────────────────────
function EmployeeProvisionDetail({ employeeId, companyId }) {
  const { balance, history, loading, error, reload } = useEmployeeProvisions(employeeId, companyId);
  const [showHistory, setShowHistory] = useState(false);

  if (loading) return <Spinner />;
  if (error)   return <Err msg={error} onRetry={reload} />;

  const totalSaldo = balance.reduce((s, b) => s + parseFloat(b.saldo_disponible || 0), 0);

  return (
    <div className="space-y-5">
      {/* Balance por tipo */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-800">Balance de Provisiones</h2>
          <div className="text-right">
            <p className="text-xs text-gray-400">Pasivo total</p>
            <p className="text-lg font-bold text-amber-600">RD$ {fmt(totalSaldo)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {balance.map(b => {
            const c = CODIGO_COLORS[b.codigo] || CODIGO_COLORS.PREV;
            const saldo = parseFloat(b.saldo_disponible || 0);
            return (
              <div key={b.provision_type_id} className={`rounded-lg border p-4 ${c.bg} ${c.border}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.badge}`}>
                    {b.codigo}
                  </span>
                  <span className="text-xs text-gray-500">{b.metodo_calculo}</span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{b.nombre}</p>
                <p className="text-xl font-bold text-gray-900">RD$ {fmt(saldo)}</p>
                <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                  <div className="flex justify-between">
                    <span>Total provisionado</span>
                    <span className="font-medium">RD$ {fmt(b.total_provisionado)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total liquidado</span>
                    <span className="font-medium">RD$ {fmt(b.total_liquidado)}</span>
                  </div>
                  {b.ultimos_meses_servicio && (
                    <div className="flex justify-between">
                      <span>Meses de servicio</span>
                      <span className="font-medium">
                        {parseFloat(b.ultimos_meses_servicio).toFixed(1)}
                      </span>
                    </div>
                  )}
                  {b.ultimo_periodo && (
                    <div className="flex justify-between">
                      <span>Último período</span>
                      <span className="font-medium">{b.ultimo_periodo}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {balance.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">
            Este empleado aún no tiene provisiones calculadas
          </p>
        )}
      </div>

      {/* Histórico de períodos */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <button
            onClick={() => setShowHistory(v => !v)}
            className="w-full flex items-center justify-between text-base font-semibold text-gray-800"
          >
            <span>Histórico por Período</span>
            <span className="text-gray-400 text-sm">{showHistory ? '▲ Ocultar' : '▼ Ver'}</span>
          </button>

          {showHistory && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 pr-3 font-semibold text-gray-600">Período</th>
                    <th className="text-left py-2 pr-3 font-semibold text-gray-600">Tipo</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-600">Salario</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-600">Meses</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-600">Período</th>
                    <th className="text-right py-2 pl-3 font-semibold text-gray-600">Acumulado</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, idx) => {
                    const c = CODIGO_COLORS[h.codigo] || CODIGO_COLORS.PREV;
                    return (
                      <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 pr-3 text-gray-600">
                          {h.periodo}{h.quincena ? ` (${h.quincena})` : ''}
                          {h.recalculado && (
                            <span className="ml-1 text-xs text-orange-500" title="Recalculado">↺</span>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${c.badge}`}>
                            {h.codigo}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600">
                          {fmt(h.salario_mensual)}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600">
                          {parseFloat(h.meses_servicio || 0).toFixed(1)}
                        </td>
                        <td className="py-2 px-3 text-right font-medium text-gray-800">
                          {fmt(h.monto_periodo)}
                        </td>
                        <td className="py-2 pl-3 text-right font-semibold text-gray-900">
                          {fmt(h.acumulado_nuevo)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 flex justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
    </div>
  );
}

function Err({ msg, onRetry }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-700">{msg}</p>
      <button onClick={onRetry} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">
        Reintentar
      </button>
    </div>
  );
}
